/**
 * LookupCategoryDropdown — Tier-1 portfolio filter for the `/` slash-command.
 *
 * Bare `/` shows the entity-type hierarchy. From the first typed character,
 * both entity types and values are searched together in two blocks:
 *   - ENTITY TYPE (top)
 *   - MATCHING VALUES (below)
 * Only blocks with matches render.
 */

import React, { useEffect, useRef } from 'react';
import Rollback from '../../../atoms/Icon/Rollback';
import type { ScoredMatch, MatchRange } from './lookupSearchAlgo';
import type { ScoredCategory } from './lookupEntityTypes';
import { formatColumnTag, getColumnTagStyleForName } from './lookupEntityTypes';

export interface LookupCategoryDropdownProps {
    scoredCategories: ScoredCategory[];
    matchingValues: ScoredMatch[];
    /** Flat highlight index: categories first, then matching values. */
    highlightIndex: number;
    onHighlight: (index: number) => void;
    onSelectCategory: (categoryId: string) => void;
    onSelectValue: (indexInMatchingValues: number) => void;
    query?: string;
    hasNoMatches?: boolean;
    /** When set, shows a Back control to return to the top-level category list. */
    contextLabel?: string | null;
    onBack?: () => void;
    className?: string;
    'data-test-id'?: string;
}

const LIST_MAX_HEIGHT = 320;

function renderHighlighted(text: string, ranges: MatchRange[]): React.ReactNode {
    if (!ranges || ranges.length === 0) return text;
    const parts: React.ReactNode[] = [];
    let cursor = 0;
    const sorted = [...ranges].sort((a, b) => a.start - b.start);
    for (let i = 0; i < sorted.length; i++) {
        const r = sorted[i];
        const start = Math.max(r.start, cursor);
        const end = Math.min(r.end, text.length);
        if (start > cursor) {
            parts.push(<span key={`p-${i}`}>{text.slice(cursor, start)}</span>);
        }
        if (end > start) {
            parts.push(
                <span
                    key={`m-${i}`}
                    className="font-semibold text-[#3B2C77] bg-[#f1edff] rounded-sm"
                >
                    {text.slice(start, end)}
                </span>,
            );
            cursor = end;
        }
    }
    if (cursor < text.length) {
        parts.push(<span key="tail">{text.slice(cursor)}</span>);
    }
    return <>{parts}</>;
}

const LookupCategoryDropdown: React.FC<LookupCategoryDropdownProps> = ({
    scoredCategories,
    matchingValues,
    highlightIndex,
    onHighlight,
    onSelectCategory,
    onSelectValue,
    query = '',
    hasNoMatches = false,
    contextLabel,
    onBack,
    className,
    'data-test-id': dataTestId,
}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
    const categoryCount = scoredCategories.length;

    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, [query]);

    useEffect(() => {
        const el = itemRefs.current[highlightIndex];
        if (el) el.scrollIntoView({ block: 'nearest' });
    }, [highlightIndex, query]);

    const showEntityTypes = scoredCategories.length > 0;
    const showMatchingValues = matchingValues.length > 0;
    const tagStyle = contextLabel ? getColumnTagStyleForName(contextLabel) : null;
    const tagLabel = contextLabel ? formatColumnTag(contextLabel) : null;

    return (
        <div
            className={`absolute bottom-full left-0 right-0 mb-2 z-50 bg-white rounded-[12px] border border-[#e8eafa] shadow-[0px_2.5px_20px_-2.5px_rgba(164,99,243,0.32)] overflow-hidden flex flex-col max-h-[min(480px,70vh)] ${
                className ?? ''
            }`}
            role="listbox"
            aria-label="Entity type"
            data-test-id={dataTestId}
            data-lookup-category-dropdown
            data-portfolio-filter
            onMouseDown={(e) => e.preventDefault()}
        >
            {onBack && (
                <div className="flex items-center gap-2 px-3 pt-3 pb-2 border-b border-[rgba(0,0,0,0.06)] shrink-0">
                    <button
                        type="button"
                        className="inline-flex items-center gap-1 shrink-0 bg-transparent border-0 p-0 cursor-pointer font-['Open_Sans'] text-[12px] leading-4 text-[rgba(0,0,0,0.65)] hover:text-[rgba(0,0,0,0.88)]"
                        onClick={onBack}
                        data-test-id={dataTestId ? `${dataTestId}-back` : undefined}
                    >
                        <Rollback size={14} color="rgba(0,0,0,0.65)" />
                        Back
                    </button>
                    {contextLabel && tagStyle && tagLabel && (
                        <span
                            className="font-['Open_Sans'] font-semibold text-[10px] leading-4 tracking-[0.04em] rounded-md px-2 py-0.5 shrink-0 whitespace-nowrap uppercase"
                            style={{ backgroundColor: tagStyle.bg, color: tagStyle.text }}
                        >
                            {tagLabel}
                        </span>
                    )}
                </div>
            )}
            <div
                ref={scrollContainerRef}
                className="overflow-y-auto flex-1 min-h-0"
                style={{ maxHeight: LIST_MAX_HEIGHT }}
            >
                {hasNoMatches ? (
                    <div
                        className="px-3 py-4 font-['Open_Sans'] text-[14px] leading-5 text-[rgba(0,0,0,0.65)]"
                        data-test-id={dataTestId ? `${dataTestId}-no-matches` : undefined}
                    >
                        No matches found for &ldquo;{query.trim()}&rdquo;.
                    </div>
                ) : (
                    <>
                        {showEntityTypes && (
                            <div>
                                <div className="px-3 pt-3 pb-2 sticky top-0 bg-white z-[1]">
                                    <p className="font-['Open_Sans'] text-[11px] font-semibold leading-4 tracking-[0.08em] text-[rgba(0,0,0,0.45)] uppercase m-0">
                                        Entity type
                                    </p>
                                </div>
                                {scoredCategories.map((scored, idx) => {
                                    const isActive = idx === highlightIndex;
                                    const tagStyle = getColumnTagStyleForName(
                                        scored.category.label,
                                    );
                                    const tagLabel = formatColumnTag(scored.category.label);
                                    return (
                                        <div
                                            key={scored.category.id}
                                            ref={(el) => {
                                                itemRefs.current[idx] = el;
                                            }}
                                            role="option"
                                            aria-selected={isActive}
                                            className={`relative flex items-center justify-between gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                                                isActive
                                                    ? 'bg-[#f5f3ff]'
                                                    : 'bg-transparent hover:bg-[#fafafa]'
                                            }`}
                                            onMouseEnter={() => onHighlight(idx)}
                                            onClick={() =>
                                                onSelectCategory(scored.category.id)
                                            }
                                            data-test-id={
                                                dataTestId
                                                    ? `${dataTestId}-option-${scored.category.id}`
                                                    : undefined
                                            }
                                        >
                                            {isActive && (
                                                <span
                                                    className="absolute left-0 top-1 bottom-1 w-[3px] rounded-r-full bg-[#7C3AED]"
                                                    aria-hidden
                                                />
                                            )}
                                            <span className="font-['Open_Sans'] font-normal text-[14px] leading-5 text-[rgba(0,0,0,0.88)] truncate flex-1 min-w-0">
                                                {renderHighlighted(
                                                    scored.category.label,
                                                    scored.ranges,
                                                )}
                                            </span>
                                            <span
                                                className="font-['Open_Sans'] font-semibold text-[10px] leading-4 tracking-[0.04em] rounded-md px-2 py-0.5 shrink-0 whitespace-nowrap uppercase"
                                                style={{
                                                    backgroundColor: tagStyle.bg,
                                                    color: tagStyle.text,
                                                }}
                                                title={tagLabel}
                                            >
                                                {tagLabel}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {showMatchingValues && (
                            <div>
                                <div className="px-3 pt-3 pb-2 sticky top-0 bg-white z-[1]">
                                    <p className="font-['Open_Sans'] text-[11px] font-semibold leading-4 tracking-[0.08em] text-[rgba(0,0,0,0.45)] uppercase m-0">
                                        Matching values
                                    </p>
                                </div>
                                {matchingValues.map((match, valueIdx) => {
                                    const flatIdx = categoryCount + valueIdx;
                                    const isActive = flatIdx === highlightIndex;
                                    const tagStyle = getColumnTagStyleForName(
                                        match.item.column_name,
                                    );
                                    const tagLabel = formatColumnTag(match.item.column_name);
                                    return (
                                        <div
                                            key={`${match.item.column_name}::${match.item.entity_name}::${valueIdx}`}
                                            ref={(el) => {
                                                itemRefs.current[flatIdx] = el;
                                            }}
                                            role="option"
                                            aria-selected={isActive}
                                            className={`relative flex items-center justify-between gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                                                isActive
                                                    ? 'bg-[#f5f3ff]'
                                                    : 'bg-transparent hover:bg-[#fafafa]'
                                            }`}
                                            onMouseEnter={() => onHighlight(flatIdx)}
                                            onClick={() => onSelectValue(valueIdx)}
                                            data-test-id={
                                                dataTestId
                                                    ? `${dataTestId}-value-${valueIdx}`
                                                    : undefined
                                            }
                                        >
                                            {isActive && (
                                                <span
                                                    className="absolute left-0 top-1 bottom-1 w-[3px] rounded-r-full bg-[#7C3AED]"
                                                    aria-hidden
                                                />
                                            )}
                                            <span className="font-['Open_Sans'] font-normal text-[14px] leading-5 text-[rgba(0,0,0,0.88)] truncate flex-1 min-w-0">
                                                {renderHighlighted(
                                                    match.item.entity_name,
                                                    match.ranges,
                                                )}
                                            </span>
                                            <span
                                                className="font-['Open_Sans'] font-semibold text-[10px] leading-4 tracking-[0.04em] rounded-md px-2 py-0.5 shrink-0 whitespace-nowrap uppercase"
                                                style={{
                                                    backgroundColor: tagStyle.bg,
                                                    color: tagStyle.text,
                                                }}
                                                title={tagLabel}
                                            >
                                                {tagLabel}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default LookupCategoryDropdown;
